// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for retrieving individual customer details from the Hardfin system.
 * Provides type-safe customer retrieval with Zod validation.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { customerSchema } from '@/customer/api/types';

/**
 * Schema for validating the response from the get customer API endpoint.
 * Includes both the customer data and metadata about the request.
 */
const customerResponseSchema = z
  .object({
    data: customerSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, customer: data }));

/**
 * Type definition for the get customer API response.
 * Contains the retrieved customer data and request metadata.
 */
type CustomerResponse = z.infer<typeof customerResponseSchema>;

/**
 * Retrieves detailed information for a specific customer from the Hardfin system.
 *
 * @param key - The unique identifier key of the customer to retrieve
 * @returns Promise resolving to the customer data and request metadata
 * @throws Will throw an error if the customer is not found, API request fails, or response validation fails
 */
export async function getCustomer(key: string): Promise<CustomerResponse> {
  const url = `/v1/customer/${key}`;
  const response = await api.get(url);

  return parseResponse(customerResponseSchema, response.data, {
    schemaName: 'Customer',
    endpoint: url,
  });
}
